/* ==========================================================================
   Row mappers for the real (Supabase) service branches.

   Supabase/PostgREST returns snake_case columns matching supabase/schema.sql
   exactly; the app's domain types (src/types/index.ts) are camelCase. These
   functions are the only place that translation happens, so every service
   file stays readable and the mapping only needs updating in one place if
   the schema changes.

   Row shapes are typed loosely (Record<string, any>) rather than against
   generated Supabase types, because generating those requires running
   `supabase gen types typescript` against an actual live project — not
   possible from this environment. Once you have the CLI set up locally,
   swapping these `any` shapes for generated types is a worthwhile
   follow-up, but not required for this to work correctly.
   ========================================================================== */

import { getSupabase } from "./supabaseClient";
import type { AccessCode, AuthUser, Bill, Estate, Meter, MeterReading, Transaction, WalletBalance } from "../types";

/* eslint-disable @typescript-eslint/no-explicit-any */
type Row = Record<string, any>;

export async function fetchProfileRow(userId: string): Promise<AuthUser> {
  const { data, error } = await getSupabase()
    .from("profiles")
    .select("id, name, email, phone, role, estate_id, created_at, estates(name)")
    .eq("id", userId)
    .single();

  if (error || !data) {
    throw new Error(error?.message ?? "Could not load your profile.");
  }

  const row = data as Row;
  return {
    id: row.id,
    name: row.name,
    email: row.email,
    phone: row.phone ?? undefined,
    role: row.role,
    estateId: row.estate_id,
    estateName: (row.estates as Row | null)?.name,
    createdAt: row.created_at,
  };
}

export function mapMeterRow(row: Row): Meter {
  return {
    id: row.id,
    serial: row.serial,
    label: row.label,
    status: row.status,
    sourceType: row.source_type,
    estateId: row.estate_id,
    linkedAt: row.linked_at,
  };
}

export function mapReadingRow(meterId: string, row: Row | null): MeterReading {
  if (!row) {
    return {
      meterId,
      voltage: null,
      amps: null,
      powerFactor: null,
      unitsRemainingKwh: null,
      estimatedDaysRemaining: null,
      lastSyncAt: null,
    };
  }
  return {
    meterId,
    voltage: row.voltage,
    amps: row.amps,
    powerFactor: row.power_factor,
    unitsRemainingKwh: row.units_remaining_kwh,
    estimatedDaysRemaining: row.estimated_days_remaining,
    lastSyncAt: row.synced_at,
  };
}

export function mapBillRow(row: Row): Bill {
  return {
    id: row.id,
    estateId: row.estate_id,
    title: row.title,
    category: row.category,
    amountKobo: row.amount_kobo,
    dueDate: row.due_date,
    status: row.status,
    paidAt: row.paid_at ?? undefined,
    residentId: row.resident_id ?? undefined,
  };
}

export function mapAccessCodeRow(row: Row): AccessCode {
  return {
    id: row.id,
    code: row.code,
    visitorName: row.visitor_name ?? "",
    category: row.category ?? "",
    gender: row.gender ?? "",
    maxPeople: row.max_people ?? 1,
    description: row.description ?? undefined,
    codeType: row.code_type ?? "one_time",
    status: row.status ?? "pending",
    expiresAt: row.expires_at ?? undefined,
    createdAt: row.created_at,
  };
}

export function mapTransactionRow(row: Row): Transaction {
  return {
    id: row.id,
    type: row.type,
    status: row.status,
    amountKobo: row.amount_kobo,
    direction: row.direction,
    description: row.description,
    reference: row.reference,
    createdAt: row.created_at,
    meterId: row.meter_id ?? undefined,
    meterSerial: row.meter_serial ?? undefined,
    units: row.units ?? undefined,
    billId: row.bill_id ?? undefined,
  };
}

export function mapWalletRow(row: Row): WalletBalance {
  return {
    estateId: row.estate_id,
    balanceKobo: row.balance_kobo,
    currency: row.currency,
    updatedAt: row.updated_at,
  };
}

export function mapEstateRow(row: Row): Estate {
  return { id: row.id, name: row.name, address: row.address ?? undefined };
}
